/**
 * Typed fetch client over the contract's route registry. Cookie session (`credentials: 'include'`),
 * CSRF header copied from the `co_csrf` cookie on state-changing routes, an idempotency key on
 * creating POSTs, and every failure surfaced as an ApiError carrying the server's request_id.
 */
import {
  CSRF_COOKIE,
  CSRF_HEADER,
  IDEMPOTENCY_HEADER,
  REQUEST_ID_HEADER,
  buildPath,
  routeMeta,
  type BodyOf,
  type ErrorCode,
  type ParamsOf,
  type QueryOf,
  type ResponseOf,
  type RouteName,
} from "@checkone/contract/client";

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | "network_error",
    message: string,
    readonly status: number,
    readonly requestId: string | null,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

type Opt<K extends string, T> = [T] extends [undefined] ? { [P in K]?: undefined } : { [P in K]: T };

export type CallOptions<N extends RouteName> = Opt<"params", ParamsOf<N>> &
  Partial<Opt<"query", QueryOf<N>>> &
  Opt<"body", BodyOf<N>> & {
    /** Reuse a key across retries of the same user action; generated when absent. */
    idempotencyKey?: string;
    signal?: AbortSignal;
    /** Multipart upload: sent instead of a JSON body. */
    formData?: FormData;
  };

function apiBase(): string {
  const configured = import.meta.env.VITE_API_BASE;
  if (configured) return configured.replace(/\/$/, "");
  return typeof window !== "undefined" ? window.location.origin : "http://localhost";
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

function newKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

export function urlFor<N extends RouteName>(name: N, params?: ParamsOf<N>, query?: QueryOf<N>): string {
  const route = routeMeta[name];
  const url = new URL(buildPath(route.path, params ?? {}), apiBase() + "/");
  if (query) {
    for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(
        k,
        typeof v === "string"
          ? v
          : typeof v === "number" || typeof v === "boolean"
            ? String(v)
            : JSON.stringify(v),
      );
    }
  }
  return url.toString();
}

export async function call<N extends RouteName>(
  name: N,
  opts: CallOptions<N> = {} as CallOptions<N>,
): Promise<ResponseOf<N>> {
  const route = routeMeta[name];
  const o = opts as {
    params?: ParamsOf<N>;
    query?: QueryOf<N>;
    body?: unknown;
    idempotencyKey?: string;
    signal?: AbortSignal;
    formData?: FormData;
  };
  const headers: Record<string, string> = { accept: "application/json" };
  if (route.csrf) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers[CSRF_HEADER] = token;
  }
  if (route.idempotent) headers[IDEMPOTENCY_HEADER] = o.idempotencyKey ?? newKey();
  let body: BodyInit | undefined;
  if (o.formData) body = o.formData;
  else if (o.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(o.body);
  } else if (route.method !== "GET" && route.method !== "DELETE") {
    headers["content-type"] = "application/json";
    body = "{}";
  }

  let res: Response;
  try {
    res = await fetch(urlFor(name, o.params, o.query), {
      method: route.method,
      headers,
      body,
      credentials: "include",
      signal: o.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(
      "network_error",
      "We could not reach CheckOne. Check your connection and try again.",
      0,
      null,
    );
  }

  const headerId = res.headers.get(REQUEST_ID_HEADER);
  let json: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const err = (json ?? {}) as {
      code?: ErrorCode;
      message?: string;
      request_id?: string;
      details?: Record<string, unknown>;
    };
    throw new ApiError(
      err.code ?? "internal",
      err.message ?? `Request failed (${res.status})`,
      res.status,
      err.request_id ?? headerId,
      err.details,
    );
  }
  return json as ResponseOf<N>;
}
